import React, { useState, useEffect } from 'react';
import MetaTags from 'react-meta-tags';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Button, Row, Col, Card, CardBody } from 'reactstrap';
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';

import '../../assets/scss/datatables.scss';
import '../../assets/scss/custom/pages/_users.scss';
import { getFirebaseBackend } from '../../helpers/firebaseHelper';
import { normalizeString } from '../../utils/';
import { getDefaultSorted, getPageOptions } from './config/tableConfig';
import { getUserColumns } from './config/userTableColumns';
import { useStatusCustomization } from 'hooks/useStatusCustomization';
import UserAvatar from 'components/UserAvatar';
import EditButton from 'components/Edit-Button';
import TableLoading from 'components/Table-Loading';

const ListUsers = () => {
  const firebaseHelper = getFirebaseBackend();
  const statusMap = useStatusCustomization();
  const { t: translate } = useTranslation();
  const { SearchBar } = Search;
  const [userData, setUserData] = useState([]);
  const [users, setUsers] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const pendingTotal = (acc, cur) => (cur.status === 'Pending' ? ++acc : acc);
  const deactivatedTotal = (acc, cur) => (cur.status === 'Deactivated' ? ++acc : acc);

  const fetchUsers = async () => {
    const usersObj = await firebaseHelper.getAllUsers();
    setUsers(usersObj);
  };

  const processUsers = async () => {
    const activeUsers = Object.values(users).filter((user) => user?.status === 'Activated');
    const processedUsers = await Promise.all(
      activeUsers.map(async (userInfo) => {
        const account = await firebaseHelper.accountName(userInfo.account);

        return {
          photo: <UserAvatar userId={userInfo.id} name={userInfo.name} />,
          id: userInfo.id,
          name: `${userInfo.name} ${userInfo.lastName}`,
          email: userInfo.email,
          account,
          type: translate(userInfo.adminStatus),
          status: statusMap[normalizeString(userInfo.status)],
          action: <EditButton link={`/edit-user?id=${userInfo.id}`} />,
        };
      }),
    );
    setUserData(processedUsers);
    setIsLoading(false);
  };

  useEffect(() => {
    if (users === undefined) return;
    if (users === null) {
      setUserData([]);
      setIsLoading(false);
      return;
    }

    processUsers();
  }, [users]);

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content" style={{ minHeight: '100vh' }}>
        <MetaTags>
          <title>{translate('List Users')} | Ectools</title>
        </MetaTags>
        <Container fluid>
          <Row>
            <Col xs="12" className="mb-4">
              <h4 className="mb-0 font-size-18">
                {translate('Users')} ({users ? Object.values(users).length : 0})
              </h4>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <PaginationProvider
                    pagination={paginationFactory(getPageOptions(userData.length))}
                    keyField="id"
                    columns={getUserColumns(translate)}
                    data={userData}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="id"
                        columns={getUserColumns(translate)}
                        data={userData}
                        search
                      >
                        {(toolkitProps) => (
                          <React.Fragment>
                            <Row className="mb-2 d-flex justify-content-between">
                              <Col md="4">
                                <div className="search-box me-2 mb-2 d-inline-block">
                                  <div className="position-relative">
                                    <SearchBar
                                      {...toolkitProps.searchProps}
                                      placeholder={translate('Search')}
                                    />
                                    <i className="bx bx-search-alt search-icon" />
                                  </div>
                                </div>
                              </Col>
                              <Col md="8" className="d-flex flex-row-reverse align-items-center">
                                <Button
                                  color="primary"
                                  className="d-flex gap-2 align-items-center"
                                  tag={Link}
                                  to="/create-user"
                                >
                                  <i className="bx bx-plus-circle" />
                                  {translate('Create new User')}
                                </Button>

                                <Link to="/list-pending">
                                  <p className="mx-4 mb-0" style={{ color: '#ADB5BD' }}>
                                    {translate('Pending Users')} (
                                    {users ? Object.values(users).reduce(pendingTotal, 0) : 0})
                                  </p>
                                </Link>
                                <Link to="/list-deactivated">
                                  <p className="mx-4 mb-0" style={{ color: '#ADB5BD' }}>
                                    {translate('Deactivated Users')} (
                                    {users ? Object.values(users).reduce(deactivatedTotal, 0) : 0})
                                  </p>
                                </Link>
                              </Col>
                            </Row>

                            <Row>
                              <Col xl="12">
                                <div className="table-responsive">
                                  {isLoading && <TableLoading />}
                                  {/* The table stays mounted: the pagination and search providers fail without it */}
                                  <div className={isLoading ? 'd-none' : ''}>
                                    <BootstrapTable
                                      keyField={'id'}
                                      responsive
                                      hover
                                      bordered={false}
                                      striped={false}
                                      defaultSorted={getDefaultSorted}
                                      classes={'table align-middle table-nowrap'}
                                      headerWrapperClasses={'thead-light'}
                                      {...toolkitProps.baseProps}
                                      {...paginationTableProps}
                                    />
                                  </div>
                                </div>
                              </Col>
                            </Row>

                            <Row className="align-items-md-center mt-30">
                              <Col className="inner-custom-pagination d-flex">
                                <div className="d-inline">
                                  <SizePerPageDropdownStandalone {...paginationProps} />
                                </div>
                                <div className="text-md-right ms-auto">
                                  <PaginationListStandalone {...paginationProps} />
                                </div>
                              </Col>
                            </Row>
                          </React.Fragment>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ListUsers;
